import { useState, useEffect, useRef } from 'react';
import { Star, ShoppingCart, Heart, Filter, Search, Grid, List, Truck, Shield, RotateCcw } from 'lucide-react';
import SEO from '../../SEO';

const RazorpayButton = ({ buttonId, productId }: { buttonId: string, productId: any }) => {
  const formContainerId = `razorpay-form-container-${productId}`;

  useEffect(() => {
    const formContainer = document.getElementById(formContainerId);
    if (!formContainer) return;

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/payment-button.js';
    script.async = true;
    script.dataset.payment_button_id = buttonId;

    const form = document.createElement('form');
    form.appendChild(script);
    
    formContainer.innerHTML = '';
    formContainer.appendChild(form);

  }, [buttonId, formContainerId]);

  return <div id={formContainerId}></div>;
};

const WellnessProducts = () => {
  const [viewMode, setViewMode] = useState('grid');
  const selectedCategory='all'
  const [priceRange, setPriceRange] = useState([0, 5001]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const products = [
    {
      id: 1,
      name: 'Dry Fruit Sugar-Free Handmade Laddus',
      price: 299,
      originalPrice: 299,
      rating: 4.8,
      reviews: 124,
      image: 'https://res.cloudinary.com/diqbny8ne/image/upload/v1759677994/yeiqlhfciglafgq4eqbc.jpg',
      category: 'yoga-equipment',
      badge: 'Best Seller',
      description: 'Dry Fruit Sugar-Free Handmade Laddus Pure | Nutritious | Guilt-Free Indulgence Experience...',
      inStock: true
    },
  ];

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesPrice = product.price >= priceRange[0] && product.price <= priceRange[1];
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesPrice && matchesSearch;
  });

  const ProductCard = ({ product }: any) => {
    const razorpayContainerRef = useRef<HTMLDivElement>(null);

    const handleBuyNow = () => {
      const razorpayButton = razorpayContainerRef.current?.querySelector('.PaymentButton-contents');
      if (razorpayButton) {
        (razorpayButton as HTMLElement).click();
      }
    };

    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
        <div className="relative">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-64 object-cover"

          />
          {product.badge && (
            <span className="absolute top-2 left-2 bg-teal-600 text-white px-2 py-1 rounded-full text-xs font-semibold">
              {product.badge}
            </span>
          )}
          <button className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors">
            <Heart className="w-4 h-4 text-gray-600 hover:text-red-500" />
          </button>
          {!product.inStock && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <span className="bg-red-600 text-white px-4 py-2 rounded-full font-semibold">Out of Stock</span>
            </div>
          )}
        </div>

        <div className="p-4">
          <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2">{product.name}</h3>
          <p className="text-sm text-gray-600 mb-3">{product.description}</p>

          <div className="flex items-center gap-1 mb-3">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-4 h-4 ${i < Math.floor(product.rating)
                    ? 'text-yellow-400 fill-current'
                    : 'text-gray-300'
                  }`}
              />
            ))}
            <span className="text-sm text-gray-600 ml-1">({product.reviews})</span>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-teal-600">₹{product.price}</span>
              {/* {product.originalPrice > product.price && (
              <span className="text-sm text-gray-500 line-through">₹{product.originalPrice}</span>
            )} */}
            </div>
            {product.originalPrice > product.price && (
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">
                {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
              </span>
            )}
          </div>

          <div className="w-full">
            {product.inStock ? (
              <>
                <div ref={razorpayContainerRef} id="razorpay-payment-button" hidden>
                  <RazorpayButton buttonId="pl_RPhyhjymS9BnzR" productId={product.id} />
                </div>
                <button onClick={handleBuyNow} className='w-full bg-teal-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors hover:bg-teal-700 cursor-pointer'>
                  Buy Now
                </button>
              </>
            ) : (
              <button
                className="w-full py-2 px-4 rounded-lg font-semibold transition-colors bg-gray-300 text-gray-500 cursor-not-allowed"
                disabled
              >
                <ShoppingCart className="w-4 h-4 inline-block mr-2" />
                Out of Stock
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Premium Wellness Products | MetaFit Wellness"
        description="Discover curated, premium wellness products — organic supplements, yoga essentials, aromatherapy, and holistic health items from MetaFit Wellness."
        keywords="wellness products, yoga equipment, organic supplements, natural wellness products India, aromatherapy, holistic items"
        url="https://www.metafitwellness.com/wellness-products"
        canonical="https://www.metafitwellness.com/wellness-products"
        image="https://www.metafitwellness.com/metafit.webp"
        schema={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "MetaFit Wellness Products",
          "description": "Premium quality wellness products for your healthy lifestyle journey.",
          "url": "https://www.metafitwellness.com/wellness-products"
        }}
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <h1 className="text-4xl font-bold mb-4">Wellness Products</h1>
          <p className="text-xl text-teal-100 mb-8">
            Premium quality wellness products for your healthy lifestyle journey
          </p>
          
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              <span>Free shipping over ₹999</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span>Quality guaranteed</span>
            </div>
            <div className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5" />
              <span>30-day returns</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search and Filters Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              
              <div className="flex items-center gap-2 border border-gray-300 rounded-lg">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-teal-600 text-white' : 'text-gray-600'} rounded-l-lg transition-colors`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-teal-600 text-white' : 'text-gray-600'} rounded-r-lg transition-colors`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Products Grid/List */}
          <div className="flex-1">
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-600">
                Showing {filteredProducts.length} of {products.length} products
              </p>
              <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500">
                <option>Sort by popularity</option>
                <option>Price: Low to High</option>
                <option>Price: High to Low</option>
                <option>Rating</option>
                <option>Newest</option>
              </select>
            </div>
            
            <div className={`grid gap-6 ${
              viewMode === 'grid' 
                ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' 
                : 'grid-cols-1'
            }`}>
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            
            {filteredProducts.length === 0 && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No products found</h3>
                <p className="text-gray-500">Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-16 bg-white rounded-lg shadow-sm p-8">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">Why Choose Our Wellness Products?</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Quality Assured</h3>
              <p className="text-sm text-gray-600">All products are lab-tested and certified for purity</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Fast Delivery</h3>
              <p className="text-sm text-gray-600">Free shipping on orders above ₹999 across India</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <RotateCcw className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Easy Returns</h3>
              <p className="text-sm text-gray-600">30-day hassle-free return policy</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Expert Curated</h3>
              <p className="text-sm text-gray-600">Products selected by wellness experts</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellnessProducts;